import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { createRequest, deleteRequest, unarchiveRequest } from '../../../shared/api/requests.api'
import { createItem, patchItem, bulkAssignProcurement } from '../../../shared/api/requestItems.api'
import { confirm } from '../../../shared/ui/confirmStore'
import { confirmRequestDeletion } from '../utils/confirmRequestDeletion'
import { toast } from '../../../shared/ui/toastStore'
import { apiErrorText } from '../../../shared/api/errorText'
import { conflictsMessage } from './useBulkAssignProcurement'
import { pickCopyableDeadline } from '@crm/shared'
import type { RequestItemPriority, RequestType } from '../../../shared/api/types'
import { useAuthStore } from '../../auth/authStore'

type RequestLike = {
  id: string
  /** Менеджер-владелец: помощник копирует заявку на того же менеджера. */
  managerId?: string | null
  client?: { id?: string; name?: string | null } | null
  type?: string
  deadline?: string | null
  notes?: string | null
  sellerEntityId?: string | null
  items?: Array<{
    id: string
    partNumber: string
    brand?: string | null
    quantity: number
    unit?: string | null
    priority: RequestItemPriority
    noteForProcurement?: string | null
    matrix?: string | null
    datasheetUrl?: string | null
  }>
}

export function useRequestActions(request: RequestLike | undefined, requestId: string | undefined) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)

  const [isDeletingRequest, setIsDeletingRequest] = useState(false)
  const [isUnarchiving, setIsUnarchiving] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [bulkAssignId, setBulkAssignId] = useState('')
  const [isBulkAssigning, setIsBulkAssigning] = useState(false)
  const [bulkPriorityId, setBulkPriorityId] = useState('')
  const [isBulkPriority, setIsBulkPriority] = useState(false)

  const handleDeleteRequest = async () => {
    if (!request) return
    // Confirm с составом («позиций N, квот M, КП K») — удаление мягкое,
    // заявка уходит в корзину на 30 дней.
    if (!(await confirmRequestDeletion(request.id, request.client?.name))) return
    setIsDeletingRequest(true)
    try {
      await deleteRequest(request.id)
      await queryClient.invalidateQueries({ queryKey: ['requests'] })
      navigate('/requests')
    } catch (e) {
      // Сервер отказывает, например, если по заявке оформлен заказ (409).
      toast.error(apiErrorText(e, 'Не удалось удалить заявку'))
    } finally {
      setIsDeletingRequest(false)
    }
  }

  const handleUnarchiveRequest = async () => {
    if (!request || isUnarchiving) return
    if (
      !(await confirm({
        title: 'Вернуть заявку из архива?',
        message: 'Заявка вернётся в статус, который был до архивации.',
        confirmText: 'Вернуть',
      }))
    )
      return
    setIsUnarchiving(true)
    try {
      await unarchiveRequest(request.id)
      await queryClient.invalidateQueries({ queryKey: ['requests'] })
      await queryClient.invalidateQueries({ queryKey: ['requests', request.id] })
    } finally {
      setIsUnarchiving(false)
    }
  }

  const handleCopyRequest = async () => {
    if (!request || isCopying) return
    if (
      !(await confirm({
        title: 'Создать копию заявки со всеми позициями?',
        confirmText: 'Создать',
      }))
    )
      return
    setIsCopying(true)
    try {
      const copy = await createRequest({
        clientId: request.client?.id ?? '',
        type: request.type as RequestType,
        // Абсурдный исторический дедлайн (вне 2000–2100) в копию не переносим,
        // иначе создание копии получит отказ.
        deadline: pickCopyableDeadline(request.deadline),
        notes: request.notes ?? null,
        // Юрлицо продажи — как у исходной (у старых заявок его нет — по умолчанию).
        ...(request.sellerEntityId ? { sellerEntityId: request.sellerEntityId } : {}),
        // Помощник обязан указать менеджера-владельца — копия остаётся за тем же
        // менеджером, что и исходная (сервер проверит, что он прикреплён).
        ...(role === 'ASSISTANT' && request.managerId ? { ownerManagerId: request.managerId } : {}),
      })
      const items = request.items ?? []
      const results = await Promise.allSettled(
        items.map((item) =>
          createItem(copy.id, {
            partNumber: item.partNumber,
            brand: item.brand ?? null,
            quantity: item.quantity,
            unit: item.unit ?? null,
            priority: item.priority,
            noteForProcurement: item.noteForProcurement ?? null,
            matrix: item.matrix ?? null,
            datasheetUrl: item.datasheetUrl ?? null,
          }),
        ),
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      await queryClient.invalidateQueries({ queryKey: ['requests'] })
      if (failed > 0) {
        toast.error(`Скопировано ${items.length - failed} из ${items.length} позиций`)
      }
      navigate(`/requests/${copy.id}`)
    } catch (e) {
      // Раньше catch не было: отказ сервера (например, клиент уже передан
      // другому менеджеру) молча возвращал кнопку из «Копирование…».
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data
        ?.error?.message
      toast.error(msg ?? 'Не удалось создать копию заявки')
    } finally {
      setIsCopying(false)
    }
  }

  const handleBulkPriority = async () => {
    if (!bulkPriorityId || isBulkPriority) return
    const allItems = request?.items ?? []
    if (allItems.length === 0) return
    setIsBulkPriority(true)
    try {
      const results = await Promise.allSettled(
        allItems.map((item) =>
          patchItem(item.id, { priority: bulkPriorityId as RequestItemPriority }),
        ),
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      await queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      setBulkPriorityId('')
      if (failed > 0) {
        toast.error(`Не удалось обновить приоритет у ${failed} из ${allItems.length} позиций`)
      }
    } finally {
      setIsBulkPriority(false)
    }
  }

  const handleBulkAssign = async () => {
    if (!bulkAssignId || isBulkAssigning || !requestId) return
    const allItems = request?.items ?? []
    if (allItems.length === 0) return
    setIsBulkAssigning(true)
    try {
      // Один bulk-запрос вместо N PATCH (не бьём rate-limit на больших заявках).
      // Бэк сам пропускает позиции с матрицей не Китай/Производители.
      const ids = allItems.map((i) => i.id)
      let res = await bulkAssignProcurement(requestId, ids, bulkAssignId)
      // Живая работа других закупщиков — подтверждение и повтор с force.
      if (res.conflicts && res.conflicts.length > 0) {
        const ok = await confirm({
          title: `У ${res.conflicts.length} поз. уже есть работа другого закупщика`,
          message: conflictsMessage(res.conflicts),
          confirmText: 'Всё равно назначить',
          danger: true,
        })
        if (!ok) {
          setIsBulkAssigning(false)
          return
        }
        res = await bulkAssignProcurement(requestId, ids, bulkAssignId, true)
      }
      const { updated, skipped } = res
      await queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      setBulkAssignId('')
      if (skipped > 0) {
        toast.error(
          `Назначено: ${updated}. Пропущено ${skipped} — назначать можно только позиции с матрицей «Китай»/«Производители».`,
        )
      } else if (updated > 0) {
        toast.success(`Назначено закупщику: ${updated} ${updated === 1 ? 'позиция' : 'позиций'}`)
      } else {
        // Всё подходит по матрице, но менять нечего — назначение уже такое.
        toast.info('Изменений нет — выбранное назначение уже установлено у этих позиций')
      }
    } finally {
      setIsBulkAssigning(false)
    }
  }

  return {
    isDeletingRequest,
    isUnarchiving,
    isCopying,
    bulkAssignId,
    setBulkAssignId,
    isBulkAssigning,
    bulkPriorityId,
    setBulkPriorityId,
    isBulkPriority,
    handleDeleteRequest,
    handleUnarchiveRequest,
    handleCopyRequest,
    handleBulkPriority,
    handleBulkAssign,
  }
}
